import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { IconButton, Tooltip } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';

import { copyToClipboard } from 'utils/clipboard';
import { PINPEO, STATUS_PILL } from 'theme/palette';

const FEEDBACK_MS = 1600;

/**
 * Botón chico para copiar un valor al portapapeles.
 * Muestra un check durante FEEDBACK_MS como confirmación.
 */
export default function CopyButton({ value, title, size }) {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef(null);

  // Limpia el timer si el componente se desmonta antes de que expire
  // (p. ej. al cerrar el panel de detalle justo después de copiar).
  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  const handleCopy = useCallback(
    async (event) => {
      // El valor suele vivir dentro de una fila clickeable.
      event.stopPropagation();

      const ok = await copyToClipboard(value);

      if (!ok) return;

      setCopied(true);
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setCopied(false), FEEDBACK_MS);
    },
    [value],
  );

  if (!value) return null;

  return (
    <Tooltip title={copied ? '¡Copiado!' : title} placement="top">
      <IconButton
        aria-label={title}
        onClick={handleCopy}
        style={{ padding: 4, flexShrink: 0 }}
      >
        {copied ? (
          <CheckIcon style={{ fontSize: size, color: STATUS_PILL.success.fg }} />
        ) : (
          <FileCopyOutlinedIcon
            style={{ fontSize: size, color: PINPEO.textMuted }}
          />
        )}
      </IconButton>
    </Tooltip>
  );
}

CopyButton.propTypes = {
  value: PropTypes.string,
  title: PropTypes.string,
  size: PropTypes.number,
};

CopyButton.defaultProps = {
  value: '',
  title: 'Copiar',
  size: 14,
};
